import { useState } from "react";

import { submit } from "../../lib/runtime";
import { useCanvasState } from "../../lib/useCanvasState";
import { AvatarSprite } from "./AvatarSprite";
import { DropProblem } from "./DropViewer";
import { avatarsOf, colourOf, entrantsOf, playingEmote, shortId, slotsOf } from "./stage";

/** Every body on the stage, and a stop on each one. The stop lands on the stage, not the roster. */
export function DropModerator({
  dropMount,
  stageMount,
}: {
  dropMount: string;
  stageMount: string;
}) {
  const roster = useCanvasState(dropMount);
  const stage = useCanvasState(stageMount);
  const [picked, setPicked] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const bodies = avatarsOf(stage.state);
  const slots = slotsOf(stage.state);
  const entrants = entrantsOf(roster.state);

  if (stage.error !== null) return <DropProblem what={stage.error} who="moderator" />;

  const stop = (id: string, mount: string, submission: string) => {
    setRefused(null);
    submit(mount, submission, id).catch((error: unknown) => {
      setRefused(error instanceof Error ? error.message : "that was refused");
    });
  };

  return (
    <main
      style={{
        position: "fixed",
        inset: 0,
        background: "#0b0d10",
        color: "#e8eaed",
        font: "13px ui-monospace, monospace",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <strong>on the stage {bodies.length}</strong>
        <span style={{ color: "rgba(255,255,255,0.55)" }}>on the roster {entrants.length}</span>
        {refused !== null && <span style={{ color: "#ff8a80" }}>{refused}</span>}
        <button
          type="button"
          onClick={() => {
            stop("all", dropMount, "sweep");
            setPicked(null);
          }}
          style={{ ...BUTTON, marginLeft: "auto", background: "#c0392b" }}
        >
          take everybody out
        </button>
      </header>

      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {bodies.map((body) => (
          <button
            key={body.id}
            type="button"
            aria-label={`select ${shortId(body.id)}`}
            onClick={() => setPicked(body.id === picked ? null : body.id)}
            style={{
              position: "absolute",
              left: `${body.x * 100}%`,
              top: `${body.y * 100}%`,
              transform: "translate(-50%, -100%)",
              background: "transparent",
              border: body.id === picked ? "2px solid #3ddc84" : "2px solid transparent",
              borderRadius: 8,
              padding: 2,
              cursor: "pointer",
            }}
          >
            <AvatarSprite
              facing={body.facing}
              colour={colourOf(body.id)}
              emote={playingEmote(body, slots)}
              height={90}
              label={shortId(body.id)}
            />
            <span style={{ display: "block", color: "rgba(255,255,255,0.6)" }}>
              {shortId(body.id)}
            </span>
          </button>
        ))}
        {bodies.length === 0 && (
          <p style={{ padding: 20, color: "rgba(255,255,255,0.5)" }}>nobody has dropped in yet</p>
        )}
      </div>

      <footer
        style={{
          padding: "10px 14px",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          gap: 12,
          alignItems: "center",
        }}
      >
        <span>{picked === null ? "pick a sprite" : `picked ${shortId(picked)}`}</span>
        <button
          type="button"
          disabled={picked === null}
          onClick={() => {
            if (picked === null) return;
            stop(picked, stageMount, "remove");
            setPicked(null);
          }}
          style={{ ...BUTTON, background: picked === null ? "#2a2f36" : "#c0392b" }}
        >
          stop this drop-in
        </button>
        <span style={{ color: "rgba(255,255,255,0.4)" }}>
          the stop reaches the stage, and the stage tells the roster, so they get their drop-in
          button back
        </span>
      </footer>
    </main>
  );
}

const BUTTON: React.CSSProperties = {
  padding: "8px 14px",
  color: "white",
  border: 0,
  borderRadius: 4,
  font: "inherit",
  cursor: "pointer",
};
